import {
  Control,
  Controller,
  FieldErrors,
  get,
  useFieldArray,
  UseFormWatch,
} from 'react-hook-form';
import { useEffect, useState } from 'react';
import { PolicyForm } from '../policy-dialog';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddCircle from '@mui/icons-material/AddCircle';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Delete from '@mui/icons-material/Delete';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface PolicyImsAccordionProps {
  control: Control<PolicyForm>;
  errors: FieldErrors<PolicyForm>;
  watch: UseFormWatch<PolicyForm>;
  disabled: boolean;
  fieldName: `actionData.${number}.userModelTemplate.ims`;
}

export default function PolicyImsAccordion({
  control,
  errors,
  disabled,
  watch,
  fieldName,
}: PolicyImsAccordionProps) {
  const [open, setOpen] = useState(false);
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: fieldName,
  });

  const menuItems = [
    { value: 'custom', label: 'Custom' },
    { value: 'home', label: 'Home' },
    { value: 'other', label: 'Other' },
    { value: 'work', label: 'Work' },
  ];

  const menuItemsTwo = [
    { value: 'aim', label: 'AOL Instant Messenger' },
    { value: 'custom_protocol', label: 'Custom' },
    { value: 'gtalk', label: 'Google Talk' },
    { value: 'icq', label: 'ICQ' },
    { value: 'jabber', label: 'Jabber' },
    { value: 'msn', label: 'MSN' },
    { value: 'net_meeting', label: 'Net Meeting' },
    { value: 'qq', label: 'QQ' },
    { value: 'skype', label: 'Skype' },
    { value: 'yahoo', label: 'Yahoo' },
  ];

  const imsErrors = get(errors, fieldName);

  // close accordion if there are no items in the accordion
  useEffect(() => {
    if (fields.length === 0) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [fields]);

  // open accordion if there is an error in the accordion
  useEffect(() => {
    if (imsErrors) {
      setOpen(true);
    }
  }, [imsErrors]);

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        if (fields.length === 0) return;
        setOpen(!open);
      }}
    >
      <AccordionSummary
        expandIcon={
          <IconButton
            disabled={fields.length === 0}
            sx={{ mr: -1 }}
            disableRipple
          >
            <ExpandMore />
          </IconButton>
        }
      >
        <Stack flexDirection="row" alignItems="center" gap={1}>
          <Typography
            sx={{
              color: (theme) =>
                imsErrors ? theme.palette.error.main : 'inherit',
            }}
          >
            Instant Messaging
          </Typography>
          <Tooltip title="Add" arrow>
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  append({
                    type: '',
                    customType: '',
                    protocol: '',
                    customProtocol: '',
                    im: '',
                    primary: false,
                  });
                }}
              >
                <AddCircle />
              </IconButton>
            </span>
          </Tooltip>
          {fields.length > 0 ? (
            <Chip label={fields.length} color="primary" size="small" />
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2 }}>
        {fields.map((item, index) => {
          const typeField = watch(`${fieldName}.${index}.type`);

          return (
            <Box key={item.id}>
              <Stack
                gap={1}
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ my: 1.5 }}
              >
                <Stack flexDirection="row" alignItems="center" gap={2}>
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`${fieldName}.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        inputRef={field.ref}
                        inputProps={{
                          id: `ims-type-${index}`,
                        }}
                        InputLabelProps={{
                          htmlFor: `ims-type-${index}`,
                        }}
                        name="ims"
                        select
                        disabled={disabled}
                        label="Type"
                        error={!!imsErrors?.[index]?.type}
                        helperText={
                          imsErrors?.[index]?.type
                            ? 'This field is required.'
                            : ''
                        }
                        sx={{
                          minWidth: 150,
                        }}
                      >
                        {menuItems.map((item) => (
                          <MenuItem value={item.value} key={item.label}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  {typeField === 'custom' && (
                    <Controller
                      rules={{ required: 'This field is required.' }}
                      name={`${fieldName}.${index}.customType`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required
                          inputRef={field.ref}
                          inputProps={{
                            id: `ims-customType-${index}`,
                          }}
                          InputLabelProps={{
                            htmlFor: `ims-customType-${index}`,
                          }}
                          disabled={disabled}
                          name="ims"
                          label="Custom Type"
                          error={!!imsErrors?.[index]?.customType}
                          helperText={
                            imsErrors?.[index]?.customType?.message
                              ? imsErrors?.[index]?.customType?.message
                              : ''
                          }
                          sx={{
                            minWidth: 225,
                          }}
                        />
                      )}
                    />
                  )}
                  <Controller
                    name={`${fieldName}.${index}.protocol`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        inputRef={field.ref}
                        inputProps={{
                          id: `ims-protocol-${index}`,
                        }}
                        InputLabelProps={{
                          htmlFor: `ims-protocol-${index}`,
                        }}
                        value={field.value || ''}
                        name={'ims'}
                        select
                        disabled={disabled}
                        label="Protocol"
                        error={!!imsErrors?.[index]?.protocol}
                        helperText={
                          imsErrors?.[index]?.protocol?.message
                            ? imsErrors?.[index]?.protocol?.message
                            : ''
                        }
                        sx={{
                          minWidth: 150,
                        }}
                      >
                        {menuItemsTwo.map((item) => (
                          <MenuItem value={item.value} key={item.label}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />

                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`${fieldName}.${index}.im`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        inputRef={field.ref}
                        error={!!imsErrors?.[index]?.im}
                        helperText={
                          imsErrors?.[index]?.im?.message
                            ? imsErrors?.[index]?.im?.message
                            : ''
                        }
                        name="ims"
                        disabled={disabled}
                        autoComplete="off"
                        label={`Im`}
                        sx={{
                          minWidth: 225,
                        }}
                      />
                    )}
                  />
                </Stack>

                {/* actions */}
                <Stack flexDirection="row" gap={2}>
                  <Tooltip title="Move Down" arrow>
                    <span>
                      <IconButton
                        disabled={index === fields.length - 1 || disabled}
                        size="small"
                        onClick={() => {
                          move(index, index + 1);
                        }}
                      >
                        <ArrowDownward />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move Up" arrow>
                    <span>
                      <IconButton
                        disabled={index === 0 || disabled}
                        size="small"
                        onClick={() => {
                          move(index, index - 1);
                        }}
                      >
                        <ArrowUpward />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <span>
                      <IconButton
                        size="small"
                        disabled={disabled}
                        onClick={() => {
                          remove(index);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
              {index !== fields.length - 1 && <Divider />}
            </Box>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
}
